/**
 * MODULE: _shared/lib/frontend/api/crmApi.ts
 * PURPOSE: HTTP client for dashboard-service shell CRM REST API (/api/v1/crm/*)
 * DEPENDENCIES: ./config (API_CONFIG, getAuthHeaders)
 * PUBLIC_API: crmList, crmGet, crmCreate, crmPatch, crmDelete, crmUpsertContactByEmail, crmEntitySearch, crmMergeAccounts, crmResolveHubAccount, crmKpis, crmPipelineRun, crmPipelineRuns, CrmConflictError, isCrmConflictError
 * RECENT: 2026-08-03 [2.2.2021] — `crmUpsertContactByEmail` for inbox → CRM extract.
 * RECENT_PRIOR: 2026-07-01 [2.2.1475] — `withSolutions` param + `recommendedSolutions` on CrmAccountPortfolioRow; `CrmSolutionRecommendation` type (Territory Solution Map).
 * RECENT_PRIOR: 2026-06-22 [2.2.1470] — crmAccountPortfolio plus CrmAccountPortfolioRow/Params for GET /crm/accounts/portfolio.
 * RECENT_PRIOR: 2026-06-04 — **crmFetch** auth preflight + **401** handling; **crmList** empty list on **401**; **crmKpis** throws **CrmHttpError(401)** for polling backoff.
 * RECENT_PRIOR: 2026-05-30 — crmResolveHubAccount for territory list id → hub AccountDrawer id.
 * RECENT_PRIOR: 2026-05-01 [2.2.770] — shell CRM HTTP client; MODULE header compliance.
 */

import { API_CONFIG, getAuthHeaders } from './config';
import { CrmHttpError, fetchWithAuthRetry } from './fetchWithAuthRetry';

const CRM_BASE = `${API_CONFIG.baseURL}/api/v1/crm`;

async function crmFetch(url: string, init?: RequestInit): Promise<Response> {
  return fetchWithAuthRetry(url, init);
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const t = await res.text();
    throw new CrmHttpError(res.status, t || res.statusText);
  }
  return (await res.json()) as T;
}

export type CrmListResponse<T = unknown> = { items: T[]; total: number };

/** Account list visibility — maps to CRM hub soft-delete (deleted_at). */
export type CrmAccountVisibility = 'active' | 'archived' | 'all';

export type CrmSearchHit = {
  id: string;
  label: string;
  type: string;
  subtitle?: string;
};

export type CrmSearchResponse = { items: CrmSearchHit[] };

/** Cross-entity picker search (contacts, deals, accounts) for CRM modals when the in-memory catalog is thin. */
export async function crmEntitySearch(params: {
  q: string;
  types?: string;
  limit?: number;
}): Promise<CrmSearchResponse> {
  const q = new URLSearchParams();
  q.set('q', params.q);
  if (params.types !== undefined) q.set('types', params.types);
  if (params.limit !== undefined) q.set('limit', String(params.limit));
  const url = `${CRM_BASE}/search?${q.toString()}`;
  const res = await crmFetch(url, { headers: getAuthHeaders() });
  return parseJson<CrmSearchResponse>(res);
}

export async function crmList<T = unknown>(
  entity: 'contacts' | 'accounts' | 'deals' | 'tasks' | 'activities',
  params?: Record<string, string | number | undefined>
): Promise<CrmListResponse<T>> {
  const q = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') q.set(k, String(v));
    }
  }
  const qs = q.toString();
  const url = qs ? `${CRM_BASE}/${entity}?${qs}` : `${CRM_BASE}/${entity}`;
  const res = await crmFetch(url, { headers: getAuthHeaders() });
  if (res.status === 401) return { items: [], total: 0 };
  return parseJson<CrmListResponse<T>>(res);
}

export type CrmHubAccount = {
  id: string;
  name: string;
  industry?: string | null;
  website?: string | null;
  employee_count?: number | null;
  status?: string;
  territoryId?: string | null;
  ownerId?: string | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
  isArchived?: boolean;
  archivedAt?: string | null;
  salesAccountId?: string | null;
  source?: string;
};

/** Link CRM hub account to public.accounts (auto-match/create or explicit salesAccountId). */
export async function crmLinkSalesRecord(
  hubAccountId: string,
  salesAccountId?: string,
): Promise<CrmHubAccount> {
  const body = salesAccountId?.trim() ? { salesAccountId: salesAccountId.trim() } : {};
  const res = await crmFetch(
    `${CRM_BASE}/accounts/${encodeURIComponent(hubAccountId)}/link-sales-record`,
    {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  return parseJson<CrmHubAccount>(res);
}

/** Restore a soft-deleted (archived) CRM hub account. */
export async function crmRestoreAccount(accountId: string): Promise<CrmHubAccount> {
  const res = await crmFetch(`${CRM_BASE}/accounts/${encodeURIComponent(accountId)}/restore`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return parseJson<CrmHubAccount>(res);
}

/** Resolve unified territory/sales list id to CRM hub account (AccountDrawer). Returns null when not found. */
export async function crmResolveHubAccount(salesOrHubAccountId: string): Promise<CrmHubAccount | null> {
  const res = await crmFetch(
    `${CRM_BASE}/accounts/resolve/${encodeURIComponent(salesOrHubAccountId)}`,
    { headers: getAuthHeaders() },
  );
  if (res.status === 404) {
    return null;
  }
  return parseJson<CrmHubAccount>(res);
}

export async function crmGet<T = unknown>(entity: string, id: string): Promise<T> {
  const res = await crmFetch(`${CRM_BASE}/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`, {
    headers: getAuthHeaders(),
  });
  return parseJson<T>(res);
}

/** Response body for **`POST /crm/accounts`** duplicate (**`409`**). */
export type CrmAccountConflictBody = {
  message: string;
  existing_id: string;
  /** Present for account dedupe; optional for deal name conflicts. */
  existing_name?: string;
};

export class CrmConflictError extends Error {
  readonly body: CrmAccountConflictBody;
  readonly status: number;

  constructor(body: CrmAccountConflictBody, status = 409) {
    super(body.message || 'Conflict');
    this.name = 'CrmConflictError';
    this.body = body;
    this.status = status;
  }
}

export function isCrmConflictError(e: unknown): e is CrmConflictError {
  return e instanceof CrmConflictError;
}

export async function crmCreate<T = unknown>(entity: string, body: unknown): Promise<T> {
  const res = await crmFetch(`${CRM_BASE}/${encodeURIComponent(entity)}`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 201 || res.ok) {
    return (await res.json()) as T;
  }
  const t = await res.text();
  if (res.status === 409 && t) {
    try {
      const j = JSON.parse(t) as Partial<CrmAccountConflictBody>;
      if (typeof j.existing_id === 'string') {
        const isAccount = typeof j.existing_name === 'string' && j.existing_name.length > 0;
        throw new CrmConflictError(
          {
            message:
              typeof j.message === 'string'
                ? j.message
                : isAccount
                  ? 'An account with this name or domain already exists.'
                  : 'A deal with this name already exists.',
            existing_id: j.existing_id,
            existing_name: j.existing_name,
          },
          409,
        );
      }
    } catch (e) {
      if (e instanceof CrmConflictError) throw e;
    }
  }
  throw new Error(t || res.statusText);
}

export type CrmContactUpsertResult = {
  created: boolean;
  contact: Record<string, unknown>;
};

/** Create or update a contact matched by LOWER(TRIM(email)). */
export async function crmUpsertContactByEmail(body: {
  email: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  companyId?: string;
  source?: string;
  jobTitle?: string;
  phone?: string;
  notes?: string;
}): Promise<CrmContactUpsertResult> {
  const res = await crmFetch(`${CRM_BASE}/contacts/upsert-by-email`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseJson<CrmContactUpsertResult>(res);
}

export async function crmPatch<T = unknown>(entity: string, id: string, body: unknown): Promise<T> {
  const res = await crmFetch(`${CRM_BASE}/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseJson<T>(res);
}

export async function crmDelete(entity: string, id: string): Promise<void> {
  const res = await crmFetch(`${CRM_BASE}/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok && res.status !== 204) {
    const t = await res.text();
    throw new Error(t || res.statusText);
  }
}

export type CrmAccountMergeResponse = {
  survivorId: string;
  survivorName: string;
  duplicateId: string;
  duplicateName: string;
  dealsMoved: number;
  contactsMoved: number;
  tasksMoved: number;
  activitiesMoved: number;
  account: Record<string, unknown>;
};

/** Merge duplicate CRM account into survivor (POST /api/v1/crm/accounts/merge). */
export async function crmMergeAccounts(payload: {
  survivorId: string;
  duplicateId: string;
}): Promise<CrmAccountMergeResponse> {
  const res = await crmFetch(`${CRM_BASE}/accounts/merge`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseJson<CrmAccountMergeResponse>(res);
}

/** Brazn Brain "Define Your Solutions" match on an account (portfolio ?withSolutions=true). */
export type CrmSolutionRecommendation = {
  solution_id: string;
  solution_name: string;
  fit_score: number; // 0–100
  confidence: number; // 0–1 (coverage of evaluable match dimensions)
  tier: 'strong' | 'moderate' | 'weak';
  why: string[];
  matched_signals: string[];
  recommended_plays: Array<{
    buyer_problem: string;
    narrative: string;
    priority: string;
  }>;
};

export type CrmAccountPortfolioRow = {
  id: string;
  name: string;
  industry?: string | null;
  ownerId?: string | null;
  territoryId?: string | null;
  salesAccountId?: string | null;
  healthTier?: 'healthy' | 'watch' | 'at_risk';
  healthScore?: number;
  healthSummary?: string | null;
  openDeals?: number;
  pipelineAmount?: number;
  nearestRenewalDate?: string | null;
  renewalDays?: number | null;
  lastTouchAt?: string | null;
  subsidiaryCount?: number;
  isParentAccount?: boolean;
  parentSalesAccountId?: string | null;
  topSignal?: string;
  topSignalReason?: string | null;
  /** Populated only when the portfolio is fetched with withSolutions=true. */
  recommendedSolutions?: CrmSolutionRecommendation[];
};

export type CrmAccountPortfolioParams = {
  ownerId?: string;
  territoryId?: string;
  signal?: string;
  q?: string;
  view?: 'flat' | 'rollup';
  teamScope?: boolean;
  withSolutions?: boolean;
  limit?: number;
  offset?: number;
};

/** My Accounts portfolio — health, pipeline, renewals, explainable signals (GET /crm/accounts/portfolio). */
export async function crmAccountPortfolio(
  params?: CrmAccountPortfolioParams,
): Promise<{ items: CrmAccountPortfolioRow[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.ownerId) q.set('ownerId', params.ownerId);
  if (params?.territoryId) q.set('territoryId', params.territoryId);
  if (params?.signal && params.signal !== 'all') q.set('signal', params.signal);
  if (params?.q) q.set('q', params.q);
  if (params?.view === 'rollup') q.set('view', 'rollup');
  if (params?.teamScope) q.set('teamScope', 'true');
  if (params?.withSolutions) q.set('withSolutions', 'true');
  if (params?.limit !== undefined) q.set('limit', String(params.limit));
  if (params?.offset !== undefined) q.set('offset', String(params.offset));
  const qs = q.toString();
  const url = qs ? `${CRM_BASE}/accounts/portfolio?${qs}` : `${CRM_BASE}/accounts/portfolio`;
  const res = await crmFetch(url, { headers: getAuthHeaders() });
  if (res.status === 401) return { items: [], total: 0 };
  return parseJson<{ items: CrmAccountPortfolioRow[]; total: number }>(res);
}

export type CrmKpisResponse = {
  totalPipeline: number;
  accountCount: number;
  accountsActive30?: number;
  activeDeals: number;
  weightedPipeline: number;
  dealsAtRisk?: number;
  dealsStuck?: number;
  dealsNoActivityData?: number;
  /** Active contracts renewing within 30 days (GET /crm/kpis). */
  contractsExpiringSoon?: number;
  currencyCode?: string;
};

export async function crmKpis(): Promise<CrmKpisResponse> {
  const res = await crmFetch(`${CRM_BASE}/kpis`, { headers: getAuthHeaders() });
  if (res.status === 401) {
    throw new CrmHttpError(401, 'Unauthorized');
  }
  return parseJson<CrmKpisResponse>(res);
}

export async function crmPipelineRun(): Promise<{ runId: string }> {
  const res = await crmFetch(`${CRM_BASE}/pipeline-generation/run`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return parseJson<{ runId: string }>(res);
}

export async function crmPipelineRuns(): Promise<{
  runs: { runId: string; status: string; createdAt: string; finishedAt?: string }[];
}> {
  const res = await crmFetch(`${CRM_BASE}/pipeline-generation/runs`, { headers: getAuthHeaders() });
  return parseJson(res);
}

export type CrmTimelineItem = {
  id: string;
  source: 'shell' | 'revops';
  type: string;
  subject: string;
  relatedEntityId: string;
  relatedEntityType: string;
  relatedEntityName?: string;
  ownerId?: string;
  ownerName?: string;
  status: string;
  notes?: string | null;
  outcome?: string | null;
  occurredAtISO: string;
  createdAtISO: string;
  updatedAtISO: string;
  readOnly?: boolean;
};

export type CrmTimelineResponse = {
  items: CrmTimelineItem[];
  total: number;
  revopsLinked: boolean;
};

export async function crmGetTimeline(params: {
  accountId?: string;
  contactId?: string;
  dealId?: string;
  limit?: number;
}): Promise<CrmTimelineResponse> {
  const q = new URLSearchParams();
  if (params.accountId) q.set('accountId', params.accountId);
  if (params.contactId) q.set('contactId', params.contactId);
  if (params.dealId) q.set('dealId', params.dealId);
  if (params.limit != null) q.set('limit', String(params.limit));
  const res = await crmFetch(`${CRM_BASE}/timeline?${q.toString()}`, { headers: getAuthHeaders() });
  return parseJson<CrmTimelineResponse>(res);
}
